import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

function defaultChapter(n) {
  return {
    title: `Chapter ${n}`,
    sections: [],
    target_words: 5000,
    min_sources: 10,
  };
}

export default function StructureEditor() {
  const nav = useNavigate();
  const [defaultStructure, setDefaultStructure] = useState({});
  const [structureName, setStructureName] = useState('');
  const [numChapters, setNumChapters] = useState(5);
  const [chapters, setChapters] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/structures');
        const def = res.structures?.default?.chapters || {};
        setDefaultStructure(def);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load structures');
      }
    })();
  }, []);

  useEffect(() => {
    const next = {};
    for (let i = 1; i <= numChapters; i++) {
      const key = `chapter_${i}`;
      next[key] = chapters[key] || defaultStructure[key] || defaultChapter(i);
    }
    setChapters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numChapters, defaultStructure]);

  const preview = useMemo(() => JSON.stringify(chapters, null, 2), [chapters]);

  function updateChapter(key, patch) {
    setChapters((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  async function save() {
    setErr(null);
    setFlash(null);
    const name = structureName.trim();
    if (!name) {
      setErr('Please enter a structure name.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/structures', { name, structure: chapters });
      setFlash('Structure saved. Redirecting to dashboard…');
      setTimeout(() => nav('/dashboard'), 1500);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to save structure');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="main-container">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          <Link to="/dashboard" className="text-muted small">
            <i className="fas fa-arrow-left me-1"></i>Back to dashboard
          </Link>
          <div className="mt-2 mb-4">
            <h1 style={{ fontWeight: 700 }}>
              <i className="fas fa-sliders me-2" style={{ color: 'var(--primary)' }}></i>
              Structure editor
            </h1>
            <p className="text-muted mb-0">
              Create a custom chapter structure for your dissertations.
            </p>
          </div>

          {err && <div className="alert alert-danger">{err}</div>}
          {flash && <div className="alert alert-success">{flash}</div>}

          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-8">
                  <label className="form-label fw-semibold">Structure name</label>
                  <input
                    className="form-control form-control-lg"
                    placeholder="e.g. My University's format"
                    value={structureName}
                    onChange={(e) => setStructureName(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Chapters</label>
                  <select
                    className="form-select form-select-lg"
                    value={numChapters}
                    onChange={(e) => setNumChapters(Number(e.target.value))}
                  >
                    {[3, 4, 5, 6, 7].map((n) => (
                      <option key={n} value={n}>
                        {n} chapters
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-3">
            {Array.from({ length: numChapters }, (_, i) => i + 1).map((i) => {
              const key = `chapter_${i}`;
              const ch = chapters[key] || defaultChapter(i);
              return (
                <div key={key} className="card">
                  <div className="card-body">
                    <h5 className="mb-3">
                      <i
                        className="fas fa-book me-2"
                        style={{ color: 'var(--primary)' }}
                      ></i>
                      Chapter {i}
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold small">Title</label>
                        <input
                          className="form-control"
                          value={ch.title}
                          onChange={(e) => updateChapter(key, { title: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold small">Target words</label>
                        <input
                          type="number"
                          className="form-control"
                          value={ch.target_words}
                          onChange={(e) =>
                            updateChapter(key, { target_words: Number(e.target.value) })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold small">Min sources</label>
                        <input
                          type="number"
                          className="form-control"
                          value={ch.min_sources}
                          onChange={(e) =>
                            updateChapter(key, { min_sources: Number(e.target.value) })
                          }
                        />
                      </div>
                    </div>
                    <label className="form-label fw-semibold small mt-3">
                      Sections <span className="text-muted fw-normal">(one per line)</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={5}
                      value={ch.sections.join('\n')}
                      onChange={(e) =>
                        updateChapter(key, {
                          sections: e.target.value
                            .split('\n')
                            .map((s) => s.trim())
                            .filter((s) => s.length > 0),
                        })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="d-flex gap-2 flex-wrap mt-4">
            <button type="button" className="btn btn-cta" onClick={save} disabled={busy}>
              <i className="fas fa-save me-1"></i>
              {busy ? ' Saving…' : ' Save custom structure'}
            </button>
            <Link to="/dashboard" className="btn btn-outline-secondary">
              Cancel
            </Link>
          </div>

          <div className="card mt-4">
            <div className="card-body">
              <h6 className="text-muted text-uppercase small mb-3" style={{ letterSpacing: 2 }}>
                Preview
              </h6>
              <pre
                style={{
                  background: 'var(--light)',
                  borderRadius: 8,
                  padding: '1rem',
                  fontSize: 12,
                  overflow: 'auto',
                  margin: 0,
                }}
              >
                {preview}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
